import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { crearDispositivo, historialDispositivo, listarDispositivos, modificarDispositivo, tiposDispositivo, verificarIdentificador } from './dispositivosApi';

/**
 * Maestro de dispositivos (PNT-SEG-0004). Alta y cambio de nombre, tipo e identificador. El identificador es el enlace
 * con Traccar: el servidor solo acepta uno que exista en Traccar 1 o 2 y que no use otro equipo, y su cambio pide motivo
 * y queda en el historial (decision de Roberto 19/10: se registran el alta y el cambio de identificador; no hay bajas).
 */
const qs = (o) => new URLSearchParams(Object.entries(o).filter(([, v]) => v !== '' && v != null)).toString();
const fechaHora = (v) => (v ? new Date(v).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' }) : '');
const ESTADOS = [{ label: 'Activo', value: 'Activo' }, { label: 'Inactivo', value: 'Inactivo' }];
const NOMBRE_CAMPO = { tipo_id: 'Tipo', identificador: 'Identificador', nombre: 'Nombre', estado: 'Estado' };
const VACIO = { dispositivo_id: null, tipoId: null, identificador: '', nombre: '', estado: 'Activo', motivo: '' };

const DispositivosMaestro = () => {
    const toast = useRef(null);
    const [filas, setFilas] = useState([]);
    const [tipos, setTipos] = useState([]);
    const [filtro, setFiltro] = useState({ texto: '', tipoId: null, estado: 'Activo' });
    const [cargando, setCargando] = useState(false);
    const [form, setForm] = useState(null);
    const [original, setOriginal] = useState(null);
    const [verif, setVerif] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [guardando, setGuardando] = useState(false);

    const avisar = (severity, detail) => toast.current && toast.current.show({ severity, summary: severity === 'error' ? 'No se pudo' : 'Listo', detail, life: 5000 });

    const buscar = useCallback(async () => {
        setCargando(true);
        try {
            setFilas(await listarDispositivos(qs(filtro)));
        } catch (e) {
            avisar('error', e.message);
        } finally {
            setCargando(false);
        }
    }, [filtro]);

    useEffect(() => {
        tiposDispositivo().then((t) => setTipos(t.map((x) => ({ label: x.nombre, value: x.id })))).catch((e) => avisar('error', e.message));
    }, []);
    useEffect(() => { buscar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const abrir = async (f) => {
        setVerif(null);
        setHistorial([]);
        if (!f) {
            setOriginal(null);
            setForm({ ...VACIO });
            return;
        }
        const datos = { dispositivo_id: f.dispositivo_id, tipoId: f.tipo_id, identificador: f.identificador || '', nombre: f.nombre || '', estado: f.estado || 'Activo', motivo: '' };
        setOriginal(datos);
        setForm(datos);
        try {
            setHistorial(await historialDispositivo(f.dispositivo_id));
        } catch (e) {
            avisar('error', e.message);
        }
    };

    const cambiaIdentificador = form && original && form.identificador.trim() !== (original.identificador || '').trim();
    const pideVerificacion = form && (!original || cambiaIdentificador);
    // el servidor vuelve a validar; aca solo se evita ofrecer guardar algo que va a rechazar
    const identificadorMalo = pideVerificacion && verif && (!verif.traccar || verif.traccar.error || (verif.usadoPor && verif.usadoPor.length > 0));

    const verificar = async () => {
        if (!form.identificador.trim()) return;
        try {
            setVerif(await verificarIdentificador(qs({ identificador: form.identificador.trim(), excepto: form.dispositivo_id })));
        } catch (e) {
            avisar('error', e.message);
        }
    };

    const guardar = async () => {
        setGuardando(true);
        try {
            if (form.dispositivo_id) {
                await modificarDispositivo(form.dispositivo_id, { tipoId: form.tipoId, identificador: form.identificador, nombre: form.nombre, motivo: form.motivo });
                avisar('success', `Dispositivo ${form.dispositivo_id} actualizado`);
            } else {
                const r = await crearDispositivo({ tipoId: form.tipoId, identificador: form.identificador, nombre: form.nombre });
                avisar('success', `Dispositivo ${r.dispositivo_id} creado`);
            }
            setForm(null);
            buscar();
        } catch (e) {
            avisar('error', e.message);
        } finally {
            setGuardando(false);
        }
    };

    const estadoTag = (f) => <Tag value={f.estado || '—'} severity={(f.estado || '').toUpperCase() === 'ACTIVO' ? 'success' : 'warning'} />;
    const auditoria = (f) => (
        <small className="text-600">
            {f.usuario_modifica ? <>Cambio: {f.usuario_modifica} · {fechaHora(f.fecha_modifica)}</> : f.usuario_crea ? <>Alta: {f.usuario_crea} · {fechaHora(f.fecha_crea)}</> : 'Sin registro (anterior a esta pantalla)'}
        </small>
    );
    const identificadorCelda = (f) => (
        <span>
            {f.identificador}
            {Number(f.repetidos) > 1 && <Tag className="ml-2" severity="danger" value={`repetido x${f.repetidos}`} />}
        </span>
    );

    const resultadoVerificacion = verif && (
        <div className="mt-2 text-sm">
            {verif.traccar && !verif.traccar.error && (
                <div className="text-green-700"><i className="pi pi-check-circle mr-1" />Existe en {verif.traccar.servidor}: «{verif.traccar.name}», ultima conexion {fechaHora(verif.traccar.lastupdate) || 'sin dato'}</div>
            )}
            {verif.traccar && verif.traccar.error && <div className="text-orange-700"><i className="pi pi-exclamation-triangle mr-1" />{verif.traccar.error}</div>}
            {!verif.traccar && <div className="text-red-700"><i className="pi pi-times-circle mr-1" />No existe en Traccar 1 ni en Traccar 2: el equipo no recibiria posiciones</div>}
            {verif.usadoPor && verif.usadoPor.map((u) => (
                <div key={u.dispositivo_id} className="text-red-700"><i className="pi pi-times-circle mr-1" />Ya lo usa el dispositivo {u.dispositivo_id} ({u.nombre})</div>
            ))}
        </div>
    );

    const pie = form && (
        <div>
            <Button label="Cancelar" className="p-button-text" onClick={() => setForm(null)} />
            <Button label={form.dispositivo_id ? 'Guardar cambios' : 'Crear dispositivo'} icon="pi pi-save" loading={guardando} onClick={guardar}
                disabled={!form.tipoId || !form.identificador.trim() || !form.nombre.trim() || identificadorMalo || (cambiaIdentificador && form.motivo.trim().length < 10)} />
        </div>
    );

    return (
        <div className="grid">
            <Toast ref={toast} />
            <div className="col-12 flex align-items-center justify-content-between flex-wrap gap-2">
                <h4 className="m-0">Dispositivos</h4>
                <Button label="Nuevo dispositivo" icon="pi pi-plus" onClick={() => abrir(null)} />
            </div>
            <div className="col-12">
                <div className="card mb-0">
                    <div className="flex flex-wrap gap-3 align-items-end mb-3">
                        <span className="flex flex-column gap-1">
                            <label htmlFor="disp-texto">Identificador, nombre o id</label>
                            <InputText id="disp-texto" value={filtro.texto} onChange={(e) => setFiltro({ ...filtro, texto: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && buscar()} />
                        </span>
                        <span className="flex flex-column gap-1">
                            <label htmlFor="disp-tipo">Tipo</label>
                            <Dropdown inputId="disp-tipo" value={filtro.tipoId} options={tipos} showClear placeholder="Todos" onChange={(e) => setFiltro({ ...filtro, tipoId: e.value })} style={{ minWidth: '12rem' }} />
                        </span>
                        <span className="flex flex-column gap-1">
                            <label htmlFor="disp-estado">Estado</label>
                            <Dropdown inputId="disp-estado" value={filtro.estado} options={ESTADOS} showClear placeholder="Todos" onChange={(e) => setFiltro({ ...filtro, estado: e.value })} />
                        </span>
                        <Button label="Buscar" icon="pi pi-search" onClick={buscar} loading={cargando} />
                    </div>
                    <DataTable value={filas} size="small" paginator rows={20} rowHover dataKey="dispositivo_id" loading={cargando}
                        onRowClick={(e) => abrir(e.data)} style={{ cursor: 'pointer' }} emptyMessage="Sin dispositivos con esos filtros" responsiveLayout="scroll">
                        <Column header="Id" field="dispositivo_id" sortable style={{ width: '6rem' }} />
                        <Column header="Tipo" field="tipo" sortable />
                        <Column header="Identificador" field="identificador" sortable body={identificadorCelda} />
                        <Column header="Nombre" field="nombre" sortable />
                        <Column header="Estado" body={estadoTag} />
                        <Column header="Ultimo reporte" body={(f) => <small>{fechaHora(f.ultima_fecha_reporte)}</small>} />
                        <Column header="Registro" body={auditoria} />
                    </DataTable>
                </div>
            </div>

            <Dialog header={form && form.dispositivo_id ? `Dispositivo ${form.dispositivo_id}` : 'Nuevo dispositivo'} visible={!!form} footer={pie}
                style={{ width: 'min(52rem, 96vw)' }} onHide={() => setForm(null)}>
                {form && (
                    <TabView>
                        <TabPanel header="Datos">
                            <div className="grid p-fluid">
                                <div className="col-12 md:col-6">
                                    <label htmlFor="disp-f-tipo">Tipo</label>
                                    <Dropdown inputId="disp-f-tipo" value={form.tipoId} options={tipos} placeholder="Elija el tipo" onChange={(e) => setForm({ ...form, tipoId: e.value })} />
                                </div>
                                <div className="col-12 md:col-6">
                                    <label htmlFor="disp-f-identificador">Identificador (el de Traccar)</label>
                                    <div className="p-inputgroup">
                                        <InputText id="disp-f-identificador" value={form.identificador} onChange={(e) => { setVerif(null); setForm({ ...form, identificador: e.target.value }); }} onBlur={() => pideVerificacion && verificar()} />
                                        <Button icon="pi pi-search" title="Verificar en Traccar" aria-label="Verificar en Traccar" onClick={verificar} disabled={!form.identificador.trim()} />
                                    </div>
                                    {pideVerificacion && resultadoVerificacion}
                                </div>
                                <div className="col-12 md:col-6">
                                    <label htmlFor="disp-f-nombre">Nombre</label>
                                    <InputText id="disp-f-nombre" value={form.nombre} maxLength={100} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
                                </div>
                                {cambiaIdentificador && (
                                    <div className="col-12">
                                        <div className="p-3 mb-2 border-round" style={{ background: 'var(--yellow-50)', border: '1px solid var(--yellow-300)' }}>
                                            Cambiar el identificador cambia de donde baja el GPS este equipo. Los viajes y bitacoras anteriores quedan
                                            con el identificador viejo. Si el ultimo digito cambia, el equipo pasa a otro hilo de descarga.
                                        </div>
                                        <label htmlFor="disp-f-motivo">Motivo del cambio (obligatorio)</label>
                                        <InputTextarea id="disp-f-motivo" rows={2} value={form.motivo} maxLength={500} onChange={(e) => setForm({ ...form, motivo: e.target.value })} />
                                    </div>
                                )}
                            </div>
                        </TabPanel>
                        {form.dispositivo_id && (
                            <TabPanel header={`Historial de identificador (${historial.length})`}>
                                <DataTable value={historial} size="small" emptyMessage="Sin altas ni cambios de identificador registrados desde esta pantalla" responsiveLayout="scroll">
                                    <Column header="Fecha" body={(h) => fechaHora(h.fecha)} style={{ width: '10rem' }} />
                                    <Column header="Accion" field="accion" />
                                    <Column header="Cambios" body={(h) => {
                                        let c = {};
                                        try { c = JSON.parse(h.cambios); } catch (e) { /* texto plano */ }
                                        return Object.entries(c).map(([k, v]) => (
                                            <div key={k}><b>{NOMBRE_CAMPO[k] || k}:</b> {v.antes == null ? '—' : String(v.antes)} → {v.despues == null ? '—' : String(v.despues)}</div>
                                        ));
                                    }} />
                                    <Column header="Motivo" field="motivo" />
                                    <Column header="Usuario" body={(h) => <span>{h.username}<br /><small className="text-500">{h.ip}</small></span>} />
                                </DataTable>
                            </TabPanel>
                        )}
                    </TabView>
                )}
            </Dialog>
        </div>
    );
};

export default DispositivosMaestro;
